import { useTranslation } from '../../../i18n';
import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Button,
    Space,
    Modal,
    Form,
    Input,
    message,
    Popconfirm,
    Tag,
    Alert
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    LockOutlined,
    UserOutlined
} from '@ant-design/icons';
import hostSharedService from '../../../../shared/services/hostSharedService';

const HttpAuthManager = ({ vhostName }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        if (vhostName) {
            loadData();
        }
    }, [vhostName]);

    const loadData = async () => {
        setLoading(true);
        try {
            const response = await hostSharedService.getHttpAuth(vhostName);
            if (response.success) {
                const rawItems = response.data?.items
                    || response.data?.users
                    || (Array.isArray(response.data) ? response.data : [])
                    || [];
                setDataSource(rawItems.map((item, index) => {
                    if (typeof item === 'string') {
                        return { user: item, key: `${item}-${index}` };
                    }
                    return {
                        ...item,
                        user: item.user || item.username || item.name || `user-${index}`,
                        key: item.user || item.username || item.name || index
                    };
                }));
            } else {
                message.error(response.error || t('Failed to load HTTP authentication list'));
                setDataSource([]);
            }
        } catch (error) {
            message.error(t('Failed to load: {{message}}', { message: error.message }));
            setDataSource([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        form.resetFields();
        setModalVisible(true);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const response = await hostSharedService.addHttpAuth(vhostName, values.user, values.password);
            if (response.success) {
                message.success(t('Added successfully'));
                setModalVisible(false);
                loadData();
            } else {
                const detail = response.details
                    ? (typeof response.details === 'string'
                        ? response.details
                        : JSON.stringify(response.details))
                    : '';
                const existing = Array.isArray(response.existing_users)
                    ? response.existing_users.join(', ')
                    : '';
                message.error(
                    `${response.error || t('Failed to add')}${
                        detail ? `：${detail}` : ''
                    }${existing ? t('; existing users: {{existing}}', { existing: existing }) : ''}`
                );
                setDataSource([]);
            }
        } catch (error) {
            if (error.errorFields) {
                return;
            }
            message.error(t('Failed to add: {{message}}', { message: error.message }));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (user) => {
        setLoading(true);
        try {
            const response = await hostSharedService.deleteHttpAuth(vhostName, user);
            if (response.success) {
                message.success(t('Deleted successfully'));
                loadData();
            } else {
                message.error(response.error || t('Deletion failed'));
                setDataSource([]);
            }
        } catch (error) {
            message.error(t('Deletion failed: {{message}}', { message: error.message }));
            setDataSource([]);
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: t('Username'),
            dataIndex: 'user',
            key: 'user',
            render: (text) => (
                <Space>
                    <UserOutlined />
                    <Tag color="blue">{text || '-'}</Tag>
                </Space>
            ),
        },
        {
            title: t('Created at'),
            dataIndex: 'created_at',
            key: 'created_at',
            width: 180,
        },
        {
            title: t('Actions'),
            key: 'action',
            width: 120,
            render: (_, record) => (
                <Popconfirm
                    title={t('Delete this HTTP authentication?')}
                    onConfirm={() => handleDelete(record.user || record.username)}
                    okText={t('OK')}
                    cancelText={t('Cancel')}
                >
                    <Button type="link" danger size="small" icon={<DeleteOutlined />}>
                        {t('Delete')}
                    </Button>
                </Popconfirm>
            ),
        },
    ];

    return (
        <Card
            title={
                <Space>
                    <LockOutlined />
                    {t('HTTP authentication management')}
                </Space>
            }
            extra={
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                >
                    {t('Add authentication')}
                </Button>
            }
        >
            <Table
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                pagination={{ pageSize: 10 }}
            />

            <Modal
                title={t('Add HTTP authentication')}
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
                confirmLoading={loading}
            >
                <Form form={form} layout="vertical">
                    <Alert
                        message={t('Description')}
                        description={t('HTTP authentication protects the entire virtual host directory. No additional path configuration is needed.')}
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                    <Form.Item
                        label={t('Username')}
                        name="user"
                        rules={[
                            { required: true, message: t('Enter a username') },
                            { pattern: /^[a-zA-Z0-9_-]+$/, message: t('Only letters, numbers, underscores, and hyphens are allowed') }
                        ]}
                    >
                        <Input placeholder={t('Username')} prefix={<UserOutlined />} />
                    </Form.Item>

                    <Form.Item
                        label={t('Password')}
                        name="password"
                        rules={[
                            { required: true, message: t('Please enter a password') },
                            { min: 6, message: t('Password must be at least 6 characters') }
                        ]}
                    >
                        <Input.Password placeholder={t('Password')} prefix={<LockOutlined />} />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default HttpAuthManager;
